import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Cookies from 'universal-cookie';
import { apiUrl, authHeaders } from '../config/api';

const cookies = new Cookies();

/* ---------------------------------------------------------------------
   Module-level store.

   The landing page mounts this hook three times (new arrivals, hot
   products, "you may also like") and listing pages once more alongside
   the header. Keeping membership here means one request per page instead
   of one per component, and — more importantly — a heart toggled in one
   rail immediately updates the same product shown in another.
   --------------------------------------------------------------------- */

let codes = [];
let inFlight = null;
let hasLoaded = false;
const subscribers = new Set();

const publish = () => {
  subscribers.forEach((notify) => notify(codes));
};

const setCodes = (next) => {
  codes = next;
  publish();
};

const load = (force = false) => {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');

  if (!token || !userId) {
    hasLoaded = true;
    setCodes([]);
    return Promise.resolve([]);
  }

  if (!force && inFlight) return inFlight;

  inFlight = fetch(apiUrl(`/api/whishlist-product-code/${userId}`), {
    method: 'GET',
    headers: authHeaders(),
  })
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then((data) => {
      hasLoaded = true;
      setCodes(data.product_code || []);
      return codes;
    })
    .catch(() => {
      // A failed membership lookup must not break the listing; the hearts
      // simply render in their "not saved" state.
      hasLoaded = true;
      setCodes([]);
      return [];
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Reset the cache when the session changes (login / logout). */
export const resetWishlistCache = () => {
  hasLoaded = false;
  inFlight = null;
  setCodes([]);
};

const useWishlist = ({ onLoginRequired } = {}) => {
  const [current, setCurrent] = useState(codes);
  const [pendingCode, setPendingCode] = useState(null);

  useEffect(() => {
    subscribers.add(setCurrent);
    setCurrent(codes);
    if (!hasLoaded) load();
    return () => {
      subscribers.delete(setCurrent);
    };
  }, []);

  const fetchWishlist = useCallback(() => load(true), []);

  const isWishlisted = useCallback(
    (productCode) => current.includes(productCode),
    [current]
  );

  const toggleWishlist = useCallback(
    async (productCode) => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('user_id') || '';

      if (!token || !userId) {
        onLoginRequired?.();
        return;
      }

      setPendingCode(productCode);
      const previous = codes;

      // Optimistic update — reverted below if the request fails.
      setCodes(
        previous.includes(productCode)
          ? previous.filter((code) => code !== productCode)
          : [...previous, productCode]
      );

      try {
        const response = await fetch(apiUrl('/api/add-whishlist'), {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            user_id: parseInt(userId, 10),
            product_code: productCode,
          }),
        });
        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();
        const without = codes.filter((code) => code !== productCode);
        setCodes(data.success ? [...without, productCode] : without);
      } catch {
        setCodes(previous);

        if (token || userId) {
          Swal.fire({
            icon: 'warning',
            confirmButtonColor: '#b63f23',
            title: 'There seems to be an issue updating your wishlist.',
          });
        } else {
          localStorage.clear();
          cookies.remove('user_id');
          onLoginRequired?.();
        }
      } finally {
        setPendingCode(null);
      }
    },
    [onLoginRequired]
  );

  return {
    codes: current,
    isWishlisted,
    toggleWishlist,
    fetchWishlist,
    pendingCode,
  };
};

export default useWishlist;
